import React, { useContext, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { AlertMessage } from '@eposnow/ui-core';
import { Box, Button, Card, CircularProgress, Typography, useTheme } from '@mui/material';
import { ReactComponent as SuccessIcon } from '../../img/success.svg';
import { ReactComponent as SuccessIconWhite } from '../../img/success-white.svg';
import { UIContext } from '../../context/UIContext';
import { BusinessInfo } from '../../types';
import { UserContext } from '../../context/UserContext';
import getBankAccountCurrencyImage from '../verification/helpers/helpers';
import { ApiContext, ErrorObject } from '../../context/ApiContext';
import ActionsContainer from '../../components/ActionsContainer';
import { API_V2_HOSTNAME_KEY } from '../../localStorageKeys';
import CardRadioWithTextAndImage from '../../components/CardRadioWithTextAndImage';
import { StepContext } from '../../context/StepContext';
import PageHeader from '../../components/pageHeaderComponents/PageHeader';
import useBusinessInformation from '../../helperHooks/useBusinessInformation';
import usePageTitle from '../../helperHooks/usePageTitle';
import ErrorLoadingData from '../../components/ErrorLoadingData';

const BankAccount = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useContext(UserContext);
  const { colorMode } = useContext(UIContext);
  const { apiFetch } = useContext(ApiContext);
  const { goToPreviousStep, goToNextStep } = useContext(StepContext);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const {
    businessInformation,
    isLoading: isLoadingBusinessInfo,
    isError,
    refetchBusinessInformation,
  } = useBusinessInformation();
  const [isMyBusinessAccount, setIsMyBusinessAccount] = useState<boolean | null>(
    businessInformation?.issueBankAccount !== null &&
      businessInformation?.issueBankAccount !== undefined
      ? businessInformation?.issueBankAccount
      : null
  );

  usePageTitle('nav.bankAccount');

  useEffect(() => {
    if (
      businessInformation?.issueBankAccount !== null &&
      businessInformation?.issueBankAccount !== undefined
    ) {
      setIsMyBusinessAccount(businessInformation?.issueBankAccount);
    }
  }, [businessInformation]);

  const { isLoading: isUpdatingBusinessInfo, mutate: updateBusinessInfo } = useMutation(
    (newBusinessInfo: BusinessInfo) =>
      apiFetch(
        `${localStorage.getItem(API_V2_HOSTNAME_KEY)}onboarding/business-info`,
        newBusinessInfo,
        'PUT'
      ),
    {
      onSuccess: () => {
        goToNextStep();
      },
      onError(error: ErrorObject) {
        if (error?.code === 401) return;
        Sentry.captureException(new Error('Error updating business information'), {
          extra: { error: JSON.stringify(error) },
        });
        setErrorMessage(t('errors.errorSavingData'));
      },
    }
  );

  return isLoadingBusinessInfo || isUpdatingBusinessInfo ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '96px',
      }}
    >
      <CircularProgress
        aria-label="Loading Data"
        aria-live="polite"
        aria-busy={isLoadingBusinessInfo || isUpdatingBusinessInfo}
        data-testid="loading-icon"
        sx={{ color: 'text.secondary' }}
      />
    </Box>
  ) : (
    <>
      <PageHeader />

      {errorMessage && (
        <AlertMessage
          type="error"
          action={() => setErrorMessage('')}
          iconAlignment="baseline"
          locale={locale}
          theme={theme}
        >
          {errorMessage}
        </AlertMessage>
      )}

      <Card
        variant="outlined"
        sx={{
          marginBottom: '3rem',
          padding: 0,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          height: '100%',
        }}
      >
        {isError && (
          <Box sx={{ width: '100%' }}>
            <ErrorLoadingData action={refetchBusinessInformation} />
          </Box>
        )}
        {!isError &&
          (businessInformation?.issueBankAccount === null ||
          businessInformation?.issueBankAccount === undefined ? (
            <>
              <Box
                sx={{
                  width: {
                    sm: '55%',
                    md: '65%',
                  },
                  padding: '2rem',
                }}
              >
                <CardRadioWithTextAndImage
                  onClickAction={() => setIsMyBusinessAccount(true)}
                  isSelected={isMyBusinessAccount}
                  optionHeaderContent={
                    <>
                      <Typography variant="h2" fontWeight={600}>
                        {t('screens.bankAccount.myBusinessAccount.title')}
                      </Typography>
                      <Typography variant="body2">
                        {t('screens.bankAccount.myBusinessAccount.description')}
                      </Typography>
                    </>
                  }
                  optionBodyContent={
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                          paddingBottom: '1rem',
                          borderBottom: `1px ${
                            colorMode === 'light'
                              ? theme?.palette?.grey?.['12p']
                              : theme?.palette?.grey?.['23p']
                          } solid`,
                        }}
                      >
                        <Typography variant="body1">
                          <strong>{t('screens.bankAccount.item1.header')} - </strong>
                          {t('screens.bankAccount.item1.description')}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                          paddingBottom: '1rem',
                          borderBottom: `1px ${
                            colorMode === 'light'
                              ? theme?.palette?.grey?.['12p']
                              : theme?.palette?.grey?.['23p']
                          } solid`,
                        }}
                      >
                        <Typography variant="body1">
                          <strong>{t('screens.bankAccount.item2.header')} - </strong>
                          {t('screens.bankAccount.item2.description')}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                          paddingBottom: '1rem',
                          borderBottom: `1px ${
                            colorMode === 'light'
                              ? theme?.palette?.grey?.['12p']
                              : theme?.palette?.grey?.['23p']
                          } solid`,
                        }}
                      >
                        <Typography variant="body1">
                          <strong>{t('screens.bankAccount.item3.header')} - </strong>
                          {t('screens.bankAccount.item3.description')}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                          paddingBottom: '1rem',
                          borderBottom: `1px ${
                            colorMode === 'light'
                              ? theme?.palette?.grey?.['12p']
                              : theme?.palette?.grey?.['23p']
                          } solid`,
                        }}
                      >
                        <Typography variant="body1">
                          <strong>{t('screens.bankAccount.item4.header')} - </strong>
                          {t('screens.bankAccount.item4.description')}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                        }}
                      >
                        <Typography variant="body1">
                          <strong>{t('screens.bankAccount.item5.header')} - </strong>
                          {t('screens.bankAccount.item5.description')}
                        </Typography>
                      </Box>
                    </>
                  }
                />

                <CardRadioWithTextAndImage
                  onClickAction={() => setIsMyBusinessAccount(false)}
                  isSelected={!isMyBusinessAccount && isMyBusinessAccount !== null}
                  optionHeaderContent={
                    <>
                      <Typography variant="h2" fontWeight={600}>
                        {t('screens.bankAccount.existingAccount.title')}
                      </Typography>
                      <Typography variant="body2">
                        {t('screens.bankAccount.existingAccount.description')}
                      </Typography>
                    </>
                  }
                />
              </Box>

              <Box
                sx={{
                  width: { sm: '45%', md: '35%' },
                  padding: '1rem',
                  backgroundColor: 'secondary.main',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2rem',
                }}
              >
                <img
                  src={getBankAccountCurrencyImage(businessInformation?.currencyIsoCode)}
                  alt=""
                  style={{
                    maxWidth: '90%',
                  }}
                />
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flexGrow: 1,
                textAlign: 'center',
                alignItems: 'center',
                gap: '1rem',
                padding: '2rem',
              }}
            >
              {colorMode === 'light' ? <SuccessIcon /> : <SuccessIconWhite />}
              <Typography variant="h1" sx={{ marginTop: '0.5rem' }}>
                {t('screens.bankAccount.alreadyChosen.cardTitle')}
              </Typography>
              <Typography variant="body1">
                {t('screens.bankAccount.alreadyChosen.cardDescription')}
              </Typography>
            </Box>
          ))}
      </Card>

      <ActionsContainer>
        <Button
          variant="outlined"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          onClick={goToPreviousStep}
        >
          {t('actions.previous')}
        </Button>

        <Button
          variant="contained"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          disabled={isMyBusinessAccount === null || isMyBusinessAccount === undefined}
          onClick={() =>
            businessInformation?.issueBankAccount !== null &&
            businessInformation?.issueBankAccount !== undefined
              ? goToNextStep()
              : updateBusinessInfo({
                  ...businessInformation,
                  issueBankAccount: isMyBusinessAccount,
                })
          }
        >
          {t('actions.next')}
        </Button>
      </ActionsContainer>
    </>
  );
};

export default BankAccount;
